import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Star, Briefcase, Shield } from 'lucide-react';
import { supabase } from '../lib/supabase';
import api from '../lib/api';
import { toast } from 'sonner';
import { Skeleton } from './ui/skeleton';

const PastEmploymentSection = ({ profile }) => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language || 'en';
  const isCebuano = currentLang === 'ceb';
  
  const getLocalizedField = (job, field) => {
    if (isCebuano && job?.[`${field}_ceb`]) {
      return job[`${field}_ceb`];
    }
    return job?.[field];
  };
  const [pastJobs, setPastJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchPastJobs();
  }, [profile]);

  const fetchPastJobs = async () => {
    if (!profile) return;
    
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) return;

      const response = await api.get('/api/jobs/past-employment', {
        headers: { Authorization: `Bearer ${session.session.access_token}` }
      });

      setPastJobs(response.data.past_jobs || []);
    } catch (error) {
      console.error('Error fetching past jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitReview = async () => {
    if (rating === 0) {
      toast.error('Please select a rating');
      return;
    }

    if (!selectedJob?.employer_details?.id) {
      toast.error('Employer information not available');
      return;
    }

    setSubmitting(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      
      await api.post('/api/reviews', {
        to_user_id: selectedJob.employer_details.id,
        rating: rating,
        comment: comment,
        context: selectedJob.source_type || 'job',  // Use source_type to determine if this is a job or gig
        context_id: selectedJob.job_id
      }, {
        headers: { Authorization: `Bearer ${session.session.access_token}` }
      });

      toast.success('Review submitted successfully!');
      setShowReviewDialog(false);
      setRating(0);
      setComment('');
      setSelectedJob(null);
      
      // Refresh the list
      fetchPastJobs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div>
        <h2 className="font-heading text-2xl font-semibold mb-4">{t('pastJobs.title')}</h2>
        <Card>
          <div className="overflow-x-auto">
            <Skeleton className="h-48" />
          </div>
        </Card>
      </div>
    );
  }

  if (pastJobs.length === 0) {
    return null; // Don't show section if no past jobs
  }

  return (
    <>
      <div>
        <h2 className="font-heading text-2xl font-semibold mb-4">
          {t('pastJobs.title')} ({pastJobs.length})
        </h2>
        <p className="text-sm text-muted-foreground mb-4">
          {t('pastJobs.description')}
        </p>
        <Card>
          <div className="overflow-x-auto max-w-full">
            <table className="w-full">
              <thead className="border-b bg-muted/30">
                <tr>
                  <th className="py-3 px-4 text-left font-semibold text-sm">{t('pastJobs.employer')}</th>
                  <th className="py-3 px-4 text-left font-semibold text-sm hidden md:table-cell">{t('tables.location')}</th>
                  <th className="py-3 px-4 text-left font-semibold text-sm">{t('pastJobs.jobTitle')}</th>
                  <th className="py-3 px-4 text-left font-semibold text-sm hidden sm:table-cell">{t('pastJobs.rating')}</th>
                  <th className="py-3 px-4 text-left font-semibold text-sm">{t('pastJobs.review')}</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {pastJobs.map((jobApp) => {
                  const employer = jobApp.employer_details;
                  const job = jobApp.job;
                  
                  return (
                    <tr key={jobApp.id} className="hover:bg-muted/50 transition-colors">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <Avatar className="w-10 h-10">
                            <AvatarFallback className="bg-primary/10 text-primary">
                              {employer?.business_name?.[0] || employer?.name?.[0] || 'E'}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="flex items-center gap-2">
                              {employer?.verified && (
                                <Shield size={14} className="text-teal-600 fill-teal-600 flex-shrink-0" title="Verified True Boholano" />
                              )}
                              <span className="font-medium">
                                {employer?.business_name || employer?.name || 'Employer'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-sm text-muted-foreground hidden md:table-cell">
                        {job?.location || employer?.town || '-'}
                      </td>
                      <td className="py-3 px-4">
                        <div className="text-sm font-medium max-w-[200px] truncate">
                          {getLocalizedField(job, 'title') || t('tables.job')}
                        </div>
                      </td>
                      <td className="py-3 px-4 hidden sm:table-cell">
                        {employer?.rating > 0 && (
                          <div className="flex items-center gap-1">
                            <Star size={14} className="fill-yellow-500 text-yellow-500" />
                            <span className="text-sm font-medium">{employer.rating.toFixed(1)}</span>
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        {jobApp.already_reviewed ? (
                          <Badge variant="secondary" className="bg-green-50 text-green-700 text-xs">
                            ✓ {t('pastJobs.done')}
                          </Badge>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedJob(jobApp);
                              setShowReviewDialog(true);
                            }}
                            data-testid={`write-employer-review-${employer?.id}`}
                          >
                            <Star size={14} className="mr-1" />
                            {t('pastJobs.write')}
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Review Dialog */}
      <Dialog open={showReviewDialog} onOpenChange={setShowReviewDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Review {selectedJob?.employer_details?.business_name || selectedJob?.employer_details?.name || 'Employer'}
            </DialogTitle>
            <DialogDescription>
              Share your experience working on "{getLocalizedField(selectedJob?.job, 'title')}"
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Star Rating */}
            <div>
              <label className="text-sm font-medium mb-2 block">Rating *</label>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setRating(star)}
                    className="transition-transform hover:scale-110"
                    data-testid={`rating-star-${star}`}
                  >
                    <Star
                      size={32}
                      className={star <= rating ? 'fill-yellow-500 text-yellow-500' : 'text-gray-300'}
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* Comment */}
            <div>
              <label className="text-sm font-medium mb-2 block">Your Review</label>
              <Textarea
                placeholder="Tell others about your experience working with this employer..."
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={4}
                data-testid="review-comment"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowReviewDialog(false);
                setRating(0);
                setComment('');
                setSelectedJob(null);
              }}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmitReview}
              disabled={submitting || rating === 0}
              data-testid="submit-employer-review-btn"
            >
              {submitting ? 'Submitting...' : 'Submit Review'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PastEmploymentSection;
